import { Alert, TextButton } from '@zillow/constellation';
import { useState } from 'react';
import {
  ApplicationStatus,
  type ReceivedCoapplicantInvitation,
  type RentalApplication,
} from '../../../graphql/queries';
import { MultipleInvitesModal } from './MultipleInvitesModal';
import { useRouter } from 'next/router';
import { getInvitations } from '../../../utils/business-logic';
import { getApplicationRouteForInvite } from '../../../constants/urls';
import { formatDisplayName } from './MultipleInvitesModal/MultipleInvitesModal';
import { getPossesiveName } from '../../../utils/formatters';

interface Props {
  application: RentalApplication;
}

export const multipleInvitesButtonText = 'Select an application to join';
export const multipleInvitesBodyText =
  'Multiple people have started an application for this listing and have listed you as a co-applicant.';

export function AlreadyInvitedAlert({ application }: Props) {
  const router = useRouter();
  const invitations = getInvitations(application);
  const applicationStatus = application.applicationStatus;
  const submitted = applicationStatus === ApplicationStatus.submitted;
  const listingAlias = application.rentalListing?.listingAlias as string;
  const [isMultipleInvitesModalOpen, setIsMultipleInvitesModalOpen] = useState(false);

  function getNameTextFromInvite(invites: Array<ReceivedCoapplicantInvitation>) {
    const primaryApplicantInfo = invites[0].invitedBy || {};
    const firstName = primaryApplicantInfo?.firstName ?? '';
    const lastName = primaryApplicantInfo?.lastName ?? '';
    const email = (primaryApplicantInfo?.email as string) ?? '';
    return formatDisplayName(firstName, lastName, email);
  }

  async function onJoinApplicationClick(rentalApplicationInviteId: string) {
    router.push(getApplicationRouteForInvite({ listingAlias, rentalApplicationInviteId }));
  }

  function getBodyText(primaryApplicantName: string) {
    if (invitations.length === 1) {
      return `${primaryApplicantName} has already started an application for this listing that lists you as a co-applicant.`;
    }
    return multipleInvitesBodyText;
  }

  function getButtonText(primaryApplicantName: string) {
    if (invitations.length === 1) {
      return `Join ${getPossesiveName(primaryApplicantName)} application`;
    }
    return multipleInvitesButtonText;
  }

  if (!submitted && invitations.length > 0) {
    const rentalApplicationInviteId = invitations[0].rentalApplicationInviteId ?? '';
    const primaryApplicantName = getNameTextFromInvite(invitations);

    return (
      <>
        <MultipleInvitesModal
          isOpen={isMultipleInvitesModalOpen}
          onCloseModal={() => setIsMultipleInvitesModalOpen(false)}
          invitations={invitations}
          onJoinApplicationClick={onJoinApplicationClick}
        />
        <Alert
          body={getBodyText(primaryApplicantName)}
          actionButton={
            <TextButton
              onClick={
                invitations.length > 1
                  ? () => setIsMultipleInvitesModalOpen(true)
                  : () => onJoinApplicationClick(rentalApplicationInviteId)
              }
              type="button"
            >
              {getButtonText(primaryApplicantName)}
            </TextButton>
          }
        />
      </>
    );
  }

  return null;
}
